import React, { FC, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { BUTTON_TYPES, EvButton } from '@evinced-private/ui-common';

import './QuickWinsPagination.scss';

enum PaginationDirection {
	NEXT = 'next',
	PREV = 'prev'
}

interface IQuickWinsPagination {
	currentPage: number;
	setCurrentPage: (currPage: number) => void;
	className?: string;
	numberOfPages?: number;
}

const QuickWinsPagination: FC<IQuickWinsPagination> = ({
	currentPage,
	setCurrentPage,
	className,
	numberOfPages = 2
}) => {
	const nextButtonRef = useRef<HTMLButtonElement>(null);
	const prevButtonRef = useRef<HTMLButtonElement>(null);
	const [lastAction, setLastAction] = useState<PaginationDirection | null>(null);

	const onNext = (): void => {
		if (currentPage < numberOfPages) {
			setLastAction(PaginationDirection.NEXT);
			setCurrentPage(currentPage + 1);
		}
	};

	const onPrev = (): void => {
		if (currentPage > 1) {
			setLastAction(PaginationDirection.PREV);
			setCurrentPage(currentPage - 1);
		}
	};

	useEffect(() => {
		if (lastAction === PaginationDirection.NEXT) {
			if (currentPage >= numberOfPages) {
				prevButtonRef.current?.focus();
			} else {
				nextButtonRef.current?.focus();
			}
		} else if (lastAction === PaginationDirection.PREV) {
			if (currentPage <= 1) {
				nextButtonRef.current?.focus();
			} else {
				prevButtonRef.current?.focus();
			}
		}
		setLastAction(null);
	}, [currentPage, lastAction, numberOfPages]);

	return (
		<div className={classNames('quick-wins-pagination', { [className]: !!className })}>
			<EvButton
				ref={prevButtonRef}
				className="prev-button"
				onClick={onPrev}
				type={BUTTON_TYPES.LINK}
				title="Prev button: Show previous elements"
				disabled={currentPage === 1}
			>
				{'< Prev'}
			</EvButton>
			<div className="page-position">{`${currentPage} of ${numberOfPages}`}</div>
			<EvButton
				ref={nextButtonRef}
				className="next-button"
				onClick={onNext}
				type={BUTTON_TYPES.LINK}
				title="Next: Show next elements"
				disabled={currentPage >= numberOfPages}
			>
				{'Next >'}
			</EvButton>
		</div>
	);
};

export { QuickWinsPagination };
